// WorkspaceProfileSchemaService.js

import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

// Firebase
import { collections, db } from 'firebaseConfig';

const WORKSPACES_COLLECTION = collections.workspaces;
const PROFILE_SCHEMA_COLLECTION = 'profileSchema';
const PROFILE_SCHEMA_DOC_KEY = 'main';

function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value
      .filter((entry) => entry !== undefined)
      .map((entry) => stripUndefinedDeep(entry));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (entryValue === undefined) {
      return accumulator;
    }

    accumulator[key] = stripUndefinedDeep(entryValue);
    return accumulator;
  }, {});
}

function profileSchemaDocument(workspaceKey) {
  return doc(db, WORKSPACES_COLLECTION, workspaceKey, PROFILE_SCHEMA_COLLECTION, PROFILE_SCHEMA_DOC_KEY);
}

function normalizeProfileSchemaFields(fields) {
  return (Array.isArray(fields) ? fields : [])
    .filter((field) => field && typeof field === 'object')
    .map((field) => ({
      ...field,
      name: String(field?.name || '').trim(),
      type: String(field?.type || '').trim(),
      label: String(field?.label || '').trim(),
    }))
    .map((field) => stripUndefinedDeep(field))
    .filter((field) => field.name && field.type && field.label);
}

export function subscribeToWorkspaceProfileSchema(workspaceKey, callback, onError) {
  const normalizedWorkspaceKey = String(workspaceKey || '').trim();

  if (!normalizedWorkspaceKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    profileSchemaDocument(normalizedWorkspaceKey),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      const data = snapshot.data() || {};

      callback({
        id: snapshot.id,
        ...data,
        schema: {
          ...(data?.schema || {}),
          fields: normalizeProfileSchemaFields(data?.schema?.fields),
        },
      });
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
      } else {
        console.error('[WorkspaceProfileSchemaService] subscribe error:', error);
      }

      callback(null);
    }
  );
}

export async function saveWorkspaceProfileSchema({
  workspaceKey,
  userKey,
  fields,
} = {}) {
  const normalizedWorkspaceKey = String(workspaceKey || '').trim();
  const normalizedUserKey = String(userKey || '').trim();

  if (!normalizedWorkspaceKey) {
    throw new Error('workspaceKey is required');
  }

  if (!normalizedUserKey) {
    throw new Error('userKey is required');
  }

  const docRef = profileSchemaDocument(normalizedWorkspaceKey);
  const snapshot = await getDoc(docRef);
  const payload = {
    key: PROFILE_SCHEMA_DOC_KEY,
    workspaceKey: normalizedWorkspaceKey,
    schema: {
      fields: normalizeProfileSchemaFields(fields),
    },
    dateModified: serverTimestamp(),
    updatedBy: normalizedUserKey,
  };

  if (!snapshot.exists()) {
    payload.dateCreated = serverTimestamp();
  }

  await setDoc(docRef, payload, { merge: true });
}
